import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const UsageCalculator = () => {
  const [monthlyMessages, setMonthlyMessages] = useState('10000');
  const [userCount, setUserCount] = useState('100');
  const [selectedPlan, setSelectedPlan] = useState('pro');
  const [calculatedCost, setCalculatedCost] = useState(0);
  const [isCalculating, setIsCalculating] = useState(false);

  const planOptions = [
    { value: 'starter', label: 'Starter Plan' },
    { value: 'pro', label: 'Pro Plan' },
    { value: 'business', label: 'Business Plan' },
    { value: 'enterprise', label: 'Enterprise Plan' }
  ];

  const planPricing = {
    starter: { base: 29, includedMessages: 10000, overageRate: 0.001 },
    pro: { base: 99, includedMessages: 100000, overageRate: 0.0008 },
    business: { base: 299, includedMessages: 500000, overageRate: 0.0006 },
    enterprise: { base: 999, includedMessages: 2000000, overageRate: 0.0004 }
  };

  const calculateCost = () => {
    setIsCalculating(true);
    
    setTimeout(() => {
      const messages = parseInt(monthlyMessages) || 0;
      const plan = planPricing?.[selectedPlan];
      
      let totalCost = plan?.base;
      
      if (messages > plan?.includedMessages) {
        const overageMessages = messages - plan?.includedMessages;
        const overageCost = overageMessages * plan?.overageRate;
        totalCost += overageCost;
      }
      
      setCalculatedCost(totalCost);
      setIsCalculating(false);
    }, 800);
  };

  useEffect(() => {
    calculateCost();
  }, [monthlyMessages, userCount, selectedPlan]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    })?.format(amount);
  };

  const getSavingsWithAnnual = () => {
    return calculatedCost * 12 * 0.2; // 20% annual discount
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
          <Icon name="Calculator" size={20} className="text-primary" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Usage Calculator</h3>
          <p className="text-sm text-muted-foreground">Estimate your monthly costs</p>
        </div>
      </div>
      <div className="space-y-4 mb-6">
        <Input
          label="Monthly Messages"
          type="number"
          placeholder="10,000"
          value={monthlyMessages}
          onChange={(e) => setMonthlyMessages(e?.target?.value)}
          description="Expected number of messages per month"
        />

        <Input
          label="Active Users"
          type="number"
          placeholder="100"
          value={userCount}
          onChange={(e) => setUserCount(e?.target?.value)}
          description="Number of active users in your application"
        />

        <Select
          label="Plan Type"
          options={planOptions}
          value={selectedPlan}
          onChange={setSelectedPlan}
          description="Select the plan you're considering"
        />
      </div>
      <div className="bg-muted/30 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-muted-foreground">Monthly Cost</span>
          {isCalculating ? (
            <div className="flex items-center space-x-2">
              <Icon name="Loader2" size={16} className="animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Calculating...</span>
            </div>
          ) : (
            <span className="text-lg font-semibold text-foreground">
              {formatCurrency(calculatedCost)}
            </span>
          )}
        </div>
        
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Annual Cost</span>
          <span className="text-foreground font-medium">
            {formatCurrency(calculatedCost * 12)}
          </span>
        </div>
        
        <div className="flex items-center justify-between text-sm mt-1">
          <span className="text-accent">Annual Savings (20%)</span>
          <span className="text-accent font-medium">
            -{formatCurrency(getSavingsWithAnnual())}
          </span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Button
          variant="outline"
          size="sm"
          iconName="FileText"
          iconPosition="left"
          className="text-muted-foreground hover:text-foreground"
        >
          View Details
        </Button>
        <Button
          variant="default"
          size="sm"
          iconName="MessageSquare"
          iconPosition="left"
        >
          Contact Sales
        </Button>
      </div>
      <div className="mt-4 p-3 bg-accent/5 border border-accent/20 rounded-lg">
        <div className="flex items-start space-x-2">
          <Icon name="Info" size={16} className="text-accent mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="text-accent font-medium mb-1">Cost Breakdown</p>
            <p className="text-muted-foreground">
              Base plan: {formatCurrency(planPricing?.[selectedPlan]?.base)} + 
              overage charges for messages beyond {planPricing?.[selectedPlan]?.includedMessages?.toLocaleString()} included
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsageCalculator;